// action/quotationActions.js
import { quotationsAPI } from './api';
import { customerAPI } from './api';
import { productAPI } from './api';

export const quotationActions = {
    // Get customers
    getCustomers: async (params = {}) => {
        try {
            const response = await customerAPI.getAll(params);
            if (response.status && response.data) {
                return { success: true, data: response.data };
            }
            return { success: false, message: 'No customers found', data: [] };
        } catch (error) {
            console.error('Error fetching customers:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to fetch customers',
                data: []
            };
        }
    },

    // Create quotation
    createQuotation: async (quotationData) => {
        try {
            if (!quotationData.customer_id) {
                return { success: false, message: 'Customer is required' };
            }
            
            let items = quotationData.items;
            if (typeof items === 'string') {
                try {
                    items = JSON.parse(items);
                } catch (e) {
                    return { success: false, message: 'Invalid items format' };
                }
            }
            
            if (!items || items.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }
            
            // Validate each item
            for (const item of items) {
                if (!item.product_name || item.product_name.trim() === '') {
                    return { success: false, message: 'Product name is required for all items' };
                }
                if (!item.quantity || parseFloat(item.quantity) <= 0) {
                    return { success: false, message: 'Valid quantity is required' };
                }
                if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
                    return { success: false, message: 'Valid unit price is required' };
                }
            }
            
            const quotationDataToSend = {
                ...quotationData,
                items: items,
                quotation_no: quotationData.quotation_no || `QTN-${Date.now()}`,
                quotation_date: quotationData.quotation_date || new Date().toISOString().split('T')[0]
            };
            
            const response = await quotationsAPI.create(quotationDataToSend);
            
            if (response && (response.success || response.status || response.id)) {
                const quotationId = response.id || response.data?.id || response.quotation_id;
                
                if (!quotationId) {
                    return { success: false, message: 'Quotation created but no ID returned' };
                }
                
                return { 
                    success: true, 
                    data: { ...response, id: quotationId, items: items },
                    message: 'Quotation created successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to create quotation' 
                };
            }
        } catch (error) {
            console.error('Error creating quotation:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to create quotation' 
            };
        }
    },

    // Get quotations list
    getQuotations: async (params = {}) => {
        try {
            const response = await quotationsAPI.list(params);
            
            if (response.data && response.data.quotations) {
                return {
                    success: true,
                    data: response.data.quotations,
                    total: response.data.total,
                    page: response.data.page,
                    limit: response.data.limit,
                    totalPages: response.data.total_pages,
                    message: 'Quotations fetched successfully'
                };
            } else if (response.quotations) {
                return {
                    success: true,
                    data: response.quotations,
                    total: response.total,
                    page: response.page,
                    limit: response.limit,
                    totalPages: response.total_pages
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format',
                    data: []
                };
            }
        } catch (error) {
            console.error('Error fetching quotations:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch quotations',
                data: []
            };
        }
    },

    // Get single quotation
    getQuotation: async (id) => {
        try {
            const response = await quotationsAPI.get(id);
            
            if (!response) {
                return { success: false, message: 'Quotation not found' };
            }
            
            if (response.success && response.data) {
                return { success: true, data: response.data };
            } else if (response.data) {
                return { success: true, data: response.data };
            } else {
                return { success: true, data: response };
            }
        } catch (error) {
            console.error('Error fetching quotation:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to fetch quotation' 
            };
        }
    },

    // Update quotation
    updateQuotation: async (id, quotationData) => {
        try {
            if (!id) {
                return { success: false, message: 'Quotation ID is required' };
            }
            
            quotationData.id = id;
            
            const response = await quotationsAPI.update(id, quotationData);
            
            if (response && (response.success || response.status || response.id)) {
                return { 
                    success: true, 
                    data: { ...response, id: id },
                    message: 'Quotation updated successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to update quotation' 
                };
            }
        } catch (error) {
            console.error('Error updating quotation:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update quotation' 
            };
        }
    },

    // Delete quotation
    deleteQuotation: async (deleteData) => {
        try {
            if (!deleteData.id && !deleteData) {
                return { success: false, message: 'Quotation ID is required' };
            }
            
            const id = typeof deleteData === 'object' ? deleteData.id : deleteData;
            const data = { id };
            
            const response = await quotationsAPI.delete(data);
            
            if (response && (response.success || response.status || response.deleted)) {
                return { 
                    success: true, 
                    data: response,
                    message: response.message || 'Quotation deleted successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to delete quotation' 
                };
            }
        } catch (error) {
            console.error('Error deleting quotation:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to delete quotation' 
            };
        }
    },

    // Update status
    updateStatus: async (id, status) => {
        try {
            const response = await quotationsAPI.updateStatus(id, status);
            return { 
                success: true, 
                data: response,
                message: `Status updated to ${status}`
            };
        } catch (error) {
            console.error('Error updating status:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update status' 
            };
        }
    },

    // Convert to sale
    convertToSale: async (id) => {
        try {
            const response = await quotationsAPI.convertToSale(id);
            return { 
                success: true, 
                data: response,
                message: 'Quotation converted to sale successfully'
            };
        } catch (error) {
            console.error('Error converting quotation:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to convert quotation' 
            };
        }
    }
};

// Product Actions for Quotations
export const quotationProductActions = {
    // Search products
    searchProducts: async (searchTerm) => {
        try {
            if (!searchTerm || searchTerm.length < 2) {
                return { success: true, data: [] };
            }
            
            const response = await productAPI.search(searchTerm);
            
            let products = [];
            if (Array.isArray(response)) {
                products = response;
            } else if (response && Array.isArray(response.data)) {
                products = response.data;
            } else if (response && response.products) {
                products = response.products;
            }
            
            const formattedProducts = products.map(product => ({
                value: product.id,
                label: `${product.product_name} (${product.sku || 'N/A'}) - ₹${product.selling_price || product.price || 0}`,
                product_name: product.product_name,
                sku: product.sku,
                selling_price: parseFloat(product.selling_price) || parseFloat(product.price) || 0,
                cost_price: parseFloat(product.cost_price) || 0,
                tax_rate: parseFloat(product.tax_rate) || 0,
                category_id: product.category_id,
                brand_id: product.brand_id,
                unit_id: product.unit_id,
                quantity: parseFloat(product.quantity) || 0,
                mrp: parseFloat(product.mrp) || 0,
                status: product.status
            }));
            
            return { success: true, data: formattedProducts };
        } catch (error) {
            console.error('Error searching products:', error);
            return { success: true, data: [] };
        }
    }
};

export default {
    quotationActions,
    quotationProductActions
};